// component-test-check-ignore
import React from 'react';
import { Icon, BoxProps, Flex, Image, Text, IconProps } from '@opendoor/bricks/core';
import { Tooltip } from '@opendoor/bricks/complex';
import { ColorScheme } from '@opendoor/bricks/system/theme';
import { useObservability } from 'shared/helpers/observability';

export interface IContentTableRow {
  title: string;
  tooltip?: {
    text: string;
    label: string;
  };
  valueHint?: string;
  valueColor?: ColorScheme;
  valueHighlightIcon?: string;
  value: string;
  trackingId: string;
  bold?: boolean;
}
interface IPassThroughContentTableRow {
  index: number;
  trackingName: string;
  rowStyle?: (index: number) => BoxProps;
  iconSize?: IconProps['size'];
  titleStyle?: BoxProps;
  valueStyle?: BoxProps;
}
/** TODO: Add documentation */
export function ContentTableRow({
  title,
  tooltip,
  valueHint,
  value,
  valueColor,
  valueHighlightIcon,
  trackingId,
  bold,
  index,
  trackingName,
  rowStyle,
  iconSize = 24,
  titleStyle,
  valueStyle,
}: IContentTableRow & IPassThroughContentTableRow) {
  const { trackEvent } = useObservability();
  return (
    <Flex
      justifyContent="space-between"
      alignItems="center"
      mt={index === 0 ? 0 : 7}
      data-testid="content-table-row-item"
      flexDirection={['column', 'row']}
      gridGap="3"
      {...(rowStyle ? rowStyle(index) : {})}
    >
      <Flex alignItems="center" flexShrink={0} maxWidth="75%">
        <Text
          fontWeight={bold ? 'semibold' : 'regular'}
          {...titleStyle}
          data-testid="content-table-row-item-title"
        >
          {title}
        </Text>
        {tooltip && (
          <Flex ml={3}>
            <Tooltip
              width={`${iconSize}px`}
              height={`${iconSize}px`}
              afterShow={() => {
                trackEvent('hover', trackingName, { trackingId });
              }}
              tooltipIcon={
                <Flex cursor="pointer" justifyContent="center" alignItems="center">
                  <Icon name="info" size={iconSize} />
                </Flex>
              }
              label={tooltip.label}
              analyticsName={`cake-content-table-${trackingId}-tooltip`}
            >
              {tooltip.text}
            </Tooltip>
          </Flex>
        )}
      </Flex>
      <Flex justifyContent="flex-end">
        {valueHint && (
          <Text size="s1" color="neutrals70" mr={5} data-testid="content-table-row-item-valuehint">
            {valueHint}
          </Text>
        )}
        {valueHighlightIcon && <Image src={valueHighlightIcon} alt="value-highlight-icon" mr={3} />}
        <Text
          size="s1"
          fontWeight={bold ? 'semibold' : 'regular'}
          data-testid="content-table-row-item-value"
          textAlign={['center', 'end']}
          color={valueColor}
          {...valueStyle}
        >
          {value}
        </Text>
      </Flex>
    </Flex>
  );
}

interface IContentTable {
  rows: IContentTableRow[];
  trackingName: IPassThroughContentTableRow['trackingName'];
  rowStyle?: IPassThroughContentTableRow['rowStyle'];
  iconSize?: IPassThroughContentTableRow['iconSize'];
  titleStyle?: IPassThroughContentTableRow['titleStyle'];
  valueStyle?: IPassThroughContentTableRow['valueStyle'];
}

/** TODO: Add documentation */
export function ContentTable({ rows, ...props }: IContentTable) {
  return (
    <Flex flexDirection="column" width="100%">
      {rows.map((row, index) => (
        <ContentTableRow key={index} index={index} {...row} {...props} />
      ))}
    </Flex>
  );
}
